<template>
  <div class="row">
    <!-- PREVIEW -->
    <div class="accordion my-3" id="jumbotron-previews">
      <div class="accordion-item">
        <h2 class="accordion-header" id="jumbotron-preview-accordion">
          <button class="accordion-button" type="button" data-bs-toggle="collapse"
            data-bs-target="#jumbotron-preview-body" aria-expanded="true" aria-controls="jumbotron-preview-body">
            <i class="fa fa-eye me-2" />
            {{ $t("backend.candidacy.components.jumbotron.preview") }}
          </button>
        </h2>
        <div id="jumbotron-preview-body" class="accordion-collapse collapse show"
          aria-labelledby="jumbotron-preview-accordion" data-bs-parent="#jumbotron-previews"  data-exclude="true">
          <div class="accordion-body p-0">
            <ul class="nav nav-pills border-bottom p-2 align-items-center" id="pills-tab" role="tablist">
              <span class="me-3 ps-2">{{ $t("backend.candidacy.components.jumbotron.languages") }}</span>
              <li v-for="(locale, i) in enabledLocales" :key="`jumbotron_preview_tab_${locale}`" class="nav-item me-2"
                role="presentation">
                <button class="nav-link py-1 px-2" :class="{ active: i === 0 }" :id="`jumbotron_preview_tab_${locale}`"
                  data-bs-toggle="pill" :data-bs-target="`#jumbotron_preview_content_${locale}`" type="button"
                  role="tab" :aria-controls="`jumbotron_preview_content_${locale}`" :aria-selected="i === 0 || null">{{
                $t(`languages.${locale}`) }}</button>
              </li>
            </ul>
            <div class="tab-content" id="pills-tabContent">
              <div v-for="(locale, i) in enabledLocales" :key="`jumbotron_preview_content_${locale}`"
                class="tab-pane fade" :class="{ show: i === 0, active: i === 0 }"
                :id="`jumbotron_preview_content_${locale}`" role="tabpanel" :aria-labelledby="`preview_tab_${locale}`"
                tabindex="0">
                <JumbotronRenderer
                  :value="{
                    primary_text_content,
                    secondary_text_content,
                    image,
                    image_description,
                    primary_btn: buttons.primary.btn,
                    primary_btn_type: buttons.primary.btn_type,
                    primary_btn_label: buttons.primary.btn_label,
                    primary_btn_href: buttons.primary.btn_href,
                    primary_btn_icon: buttons.primary.btn_icon,
                    secondary_btn: buttons.secondary.btn,
                    secondary_btn_type: buttons.secondary.btn_type,
                    secondary_btn_label: buttons.secondary.btn_label,
                    secondary_btn_href: buttons.secondary.btn_href,
                    secondary_btn_icon: buttons.secondary.btn_icon,
                    layout,
                    direction,
                    spacing,
                  }"
                  :locale="locale"
                  :hovering="hovering"
                  edit-view
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BUILDER -->
    <div class="accordion my-3" id="jumbotron-configurations">
      <!-- Main text configuration -->
      <JumbotronConfigTab id="primary-text" @enter="hovering = 'main'" @leave="hovering = ''" no-margin>
        <template v-slot:header>
          <i :class="`fa fa-${direction === 'ltr' ? 'align-left' : 'align-right'} me-2`" />
          <span>{{ $t("backend.candidacy.components.jumbotron.main_text") }}</span>
        </template>
        <template v-slot:content>
          <LocaleRichTextField
            :hide-label="true"
            :scope="[ ...scope, 'primary_text_content']"
            :initial-value="primary_text_content"
            :locales="enabledLocales"
            jumbotron-config
            @update="primary_text_content = $event"
          />
        </template>
      </JumbotronConfigTab>

      <!-- Secondary text configuration -->
      <JumbotronConfigTab id="secondary-text" @enter="hovering = 'secondary'" @leave="hovering = ''" no-margin>
        <template v-slot:header>
          <i class="fa fa-align-center me-2" />
          <span>{{ $t("backend.candidacy.components.jumbotron.secondary_text") }}</span>
        </template>
        <template v-slot:content>
          <LocaleRichTextField
            :hide-label="true"
            :scope="[ ...scope, 'secondary_text_content']"
            :initial-value="secondary_text_content"
            :locales="enabledLocales"
            jumbotron-config
            @update="secondary_text_content = $event"
          />
        </template>
      </JumbotronConfigTab>

      <!-- Image configuration -->
      <JumbotronConfigTab id="image" @enter="hovering = 'image'" @leave="hovering = ''">
        <template v-slot:header>
          <i class="fa fa-image me-2" />
          <span>{{ $t("backend.candidacy.components.jumbotron.image") }}</span>
        </template>
        <template v-slot:content>
          <ImageFileField
            v-model:image-url="image.url"
            :scope="[...scope, 'image']"
            :jumbotron-preview-layout="{ direction: direction, layout: layout }"
          />
          <LocaleStringField
            :scope="[ ...scope, 'image_description']"
            :locales="enabledLocales"
            :initial-value="image_description"
            :customLabel="$t('backend.candidacy.components.jumbotron.image_description')"
            disable-margin
            @update="image_description = $event"
          />
        </template>
      </JumbotronConfigTab>

      <!-- Call to Action configuration -->
      <JumbotronConfigTab id="cta" @enter="hovering = 'cta'" @leave="hovering = ''">
        <template v-slot:header>
          <i class="fa fa-toggle-on me-2" />
          <span>{{ $t("backend.candidacy.components.jumbotron.cta") }}</span>
        </template>
        <template v-slot:content>
          <div class="d-flex flex-column gap-4">
            <!-- Primary button -->
            <JumbotronButtonConfig
              v-model:button="buttons.primary"
              :enabled-locales="enabledLocales"
              hierarchy="primary"
              :scope="scope"
            />
            
            <!-- Secondary button -->
            <JumbotronButtonConfig
              v-model:button="buttons.secondary"
              :enabled-locales="enabledLocales"
              hierarchy="secondary"
              :scope="scope"
            />
          </div>
        </template>
      </JumbotronConfigTab>

      <!-- Styling -->
      <JumbotronConfigTab id="header-styling" @enter="hovering = 'all'" @leave="hovering = ''" no-margin>
        <template v-slot:header>
          <i class="fa fa-palette me-2" />
          <span>{{ $t("backend.candidacy.components.jumbotron.styling") }}</span>
        </template>
        <template v-slot:content>
            <!-- Layout Proportions -->
            <JumbotronLayoutProportions
              v-model:layout="layout"
              :scope="scope"
              :direction="direction"
            />

            <!-- Layout styling -->
            <div class="row m-3">
              <div class="col-6 p-0">
                <JumbotronOrientationSelector
                  v-model:direction="direction"
                  :scope="scope"
                />
              </div>

              <div class="col-6 pe-0">
                <JumbotronSpacingConfig
                  v-model:spacing="spacing"
                  :scope="scope"
                />
              </div>
            </div>
        </template>
      </JumbotronConfigTab>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  JumbotronItem,
  LayoutOrientation,
  LayoutProportions,
  Locale,
  LocalString,
  PageItem,
  PropType,
  ButtonConfig,
  CandidacyPageBlockImage,
} from "@/types";
import JumbotronRenderer from "@/components/shared/JumbotronRenderer.vue";
import LocaleRichTextField from "@/components/backend/candidacy/form_builder/form_items/shared/LocaleRichTextField.vue";
import LocaleStringField from "@/components/backend/candidacy/form_builder/form_items/shared/LocaleStringField.vue";
import { scopeToId, scopeToName } from "@/lib/candidacy/form_builder/utils";
import CheckBoxField from "../../form_builder/form_items/shared/CheckBoxField.vue";
import JumbotronConfigTab from "./jumbotron/JumbotronConfigTab.vue";
import JumbotronButtonConfig from "./jumbotron/JumbotronButtonConfig.vue";
import JumbotronLayoutProportions from "./jumbotron/JumbotronLayoutProportions.vue";
import JumbotronOrientationSelector from "./jumbotron/JumbotronOrientationSelector.vue";
import JumbotronSpacingConfig from "./jumbotron/JumbotronSpacingConfig.vue";
import ImageFileField from "@/components/backend/candidacy/form_builder/form_items/shared/ImageFileField.vue";

export default defineComponent({
  name: "Jumbotron",
  components: {
    ImageFileField,
    CheckBoxField,
    JumbotronRenderer,
    LocaleRichTextField,
    LocaleStringField,
    JumbotronConfigTab,
    JumbotronButtonConfig,
    JumbotronLayoutProportions,
    JumbotronOrientationSelector,
    JumbotronSpacingConfig,
  },
  props: {
    pageBlock: {
      type: Object as PropType<PageItem>,
      default: null,
    },
    enabledLocales: {
      type: Array as PropType<Locale[]>,
      required: true,
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    }
  },
  data({ pageBlock }): JumbotronItem {
    const pageBlockData: JumbotronItem = pageBlock.data || {};
    return {
      primary_text_content: pageBlockData.primary_text_content || {} satisfies LocalString,
      secondary_text_content: pageBlockData.secondary_text_content || {} satisfies LocalString,
      image: pageBlock.image || { url: "", thumbnail: { url: "" }} satisfies CandidacyPageBlockImage,
      image_description: pageBlockData.image_description || {} satisfies LocalString,
      buttons: {
        primary: {
          btn: pageBlockData.primary_btn ?? false,
          btn_type: pageBlockData.primary_btn_type || "signup",
          btn_label: pageBlockData.primary_btn_label || {},
          btn_href: pageBlockData.primary_btn_href || {},
          btn_icon: pageBlockData.primary_btn_icon || "",
        } satisfies ButtonConfig,
        secondary: {
          btn: pageBlockData.secondary_btn ?? false,
          btn_type: pageBlockData.secondary_btn_type || "candidates",
          btn_label: pageBlockData.secondary_btn_label || {},
          btn_href: pageBlockData.secondary_btn_href || {},
          btn_icon: pageBlockData.secondary_btn_icon || "",
        } satisfies ButtonConfig,
      },
      layout: pageBlockData.layout || ["40", "60"] satisfies LayoutProportions,
      direction: pageBlockData.direction || "ltr" satisfies LayoutOrientation,
      spacing: pageBlockData.spacing || 16,
      hovering: "",
    }
  },
  methods: {
    scopeToName,
    scopeToId,
  },
});
</script>
